import React, { useState } from "react";
import DataTable from "react-data-table-component";
import styled from "styled-components";
import Button from "../../Generic/Button";
import { errorNotification, successNotification } from "../../Generic/Notifications";
import InputBox from "../../Generic/InputBox";
import { deepCopy, isEmpty } from "../../util/util";
import RepenishmentPlanService from "../Services/ReplenishmentPlanService";
import { Autosave } from "react-autosave";
import { constrainedCustomDataTableStyles, customDataTableStyles } from "../../InventoryPlan/Styles/DataTableStyles";
import { parseMetaData } from "../../InventoryPlan/Services/InventoryPlanErrorHandlers";
import Checkbox from "../../Generic/Checkbox";
import { DEFAULT_QUANTITY_UNIT } from "../../config/app.config";

const Page = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 2rem;
	gap: 2rem;
	overflow-x: hidden;
	overflow-y: hidden;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	font-weight: 600;
`;

const Block = styled.div`
	width: max-content;
	height: auto;
	display: flex;
	gap: ${(props) => props.gap || "1rem"};
	align-items: center;
`;

const Seperator = styled.div`
	border-left: 1px solid #000;
	flex: 1;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	flex-wrap: ${(props) => props.flexWrap};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Scrollable = styled.div.attrs({
	className: "no-scrollbar",
})`
	overflow-y: scroll;
`;

const MODULE_TYPES = {
	DISPENSER: 1,
	PLATE_RACKS: 2,
};

const DISPENSER_TYPES = {
	CONVEYOR: 20,
	PUMP: 30,
};

const TABS = {
	CONVEYOR_DISPENSERS: "CONVEYOR_DISPENSERS",
	PUMP_DISPENSERS: "PUMP_DISPENSERS",
	EPRS: "EPRS",
};

function generateTableColumns(tab, { updateTableData, erroredItems, removeErroredItem }) {
	switch (tab) {
		case TABS.CONVEYOR_DISPENSERS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					center: true,
				},
				{
					name: "Mapping",
					cell: (data) => <img height={"80px"} src={data.mappingImage} alt="dispenser-mapping" />,
					grow: 1,
					center: true,
				},
				{
					name: "Container Type",
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.containerType,
					center: true,
				},
				{
					name: "Base Type",
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.baseType,
					center: true,
				},
				{
					name: "Spout Type",
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.spoutType,
					center: true,
				},
				{
					name: "Ingredient",
					selector: (data) => data.name,
					allowOverflow: false,
					center: true,
				},
				{
					name: "Amount to be filled",
					cell: (data) => (
						<span>
							{data.amountToBeFilled || 0} {DEFAULT_QUANTITY_UNIT}
						</span>
					),
					center: true,
				},
				{
					name: "Filled Amount",
					cell: (data) => {
						const key = `${data.moduleId}-${data.systemId}`;
						let hasErrored = false,
							errorMessage = "";
						if (erroredItems?.[key]) {
							hasErrored = true;
							errorMessage = erroredItems[key];
						}

						return (
							<>
								<InputBox
									errored={hasErrored}
									errorMessage={errorMessage}
									width="100%"
									type="number"
									defaultValue={data?.filledAmount}
									onChange={(e) => {
										if (hasErrored) {
											removeErroredItem(key);
										}
										updateTableData(tab, data.moduleId, data.systemId, "filledAmount", e.target.valueAsNumber);
									}}
								/>
								<div style={{ fontSize:"16px", marginLeft:"0.5rem" }} >{DEFAULT_QUANTITY_UNIT}</div>
							</>
						);
					},
					center: true,
				},
			];

		case TABS.PUMP_DISPENSERS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					center: true,
				},
				{
					name: "Mapping",
					cell: (data) => <img height={"80px"} src={data.mappingImage} alt="dispenser-mapping" />,
					grow: 1,
					center: true,
				},
				{
					name: "Agitation Required",
					cell: (data) => {
						if (!data.itemId) return;

						const defaultValue = data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.agitator;
						if (typeof defaultValue !== "undefined") {
							if (typeof defaultValue === "object") {
								return JSON.stringify(defaultValue);
							} else {
								return defaultValue === true ? "Yes" : "No";
							}
						}
					},
					center: true,
				},
				{
					name: "Ingredient",
					selector: (data) => data.name,
					allowOverflow: false,
					center: true
				},
				{
					name: "Amount to be filled",
					cell: (data) => (
						<span>
							{data.amountToBeFilled ?? 0} {DEFAULT_QUANTITY_UNIT}
						</span>
					),
					center: true
				},
				{
					name: "Filled Amount",
					cell: (data) => {
						const key = `${data.moduleId}-${data.systemId}`;
						let hasErrored = false,
							errorMessage = "";
						if (erroredItems?.[key]) {
							hasErrored = true;
							errorMessage = erroredItems[key];
						}

						return (
							<>
								<InputBox
									errored={hasErrored}
									errorMessage={errorMessage}
									width="100%"
									type="number"
									defaultValue={data?.filledAmount}
									onChange={(e) => {
										if (hasErrored) {
											removeErroredItem(key);
										}
										updateTableData(tab, data.moduleId, data.systemId, "filledAmount", e.target.valueAsNumber);
									}}
								/>
								<div style={{ fontSize:"16px", marginLeft:"0.5rem" }} >{DEFAULT_QUANTITY_UNIT}</div>
							</>
						);
					},
					center: true,
					grow: 1,
				},
			];

		case TABS.EPRS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					center: true,
				},
				// {
				// 	name: "Module Shape",
				// 	cell: (data) => <img height={"80px"} src={data.moduleView} alt="module-symbol" />,
				// 	grow: 2,
				// },
				{
					name: "Ingredient",
					selector: (data) => data.name,
					allowOverflow: true,
					center: true,
				},
				{
					name: "Action",
					cell: (data) => {
						return (
							<Div gap="1rem">
								<Div alignItem="center">
									<Checkbox
										defaultValue={data.fillAll}
										onClick={(e) => updateTableData(tab, data.moduleId, null, "fillAll", e.target.checked)}
									/>
									&nbsp; Filled {data.availableAmount || 0} Bowls
								</Div>
								<Seperator />
								<Div alignItem="center">
									<Checkbox />
									&nbsp; Packed {data.availableAmount || 0} Bowls
								</Div>
							</Div>
						);
					},
					center: true,
				},
			];

		default:
			return [];
	}
}

const UpdateInventoryPlan = (props) => {
	const { inventoryPlan, workflowId, setNeedsUpdate } = props;

	const tableDataPayload = prepareDataTablePayload(inventoryPlan);
	const [tableData, setTableData] = useState(tableDataPayload);
	const [activeTab, setActiveTab] = useState(TABS.CONVEYOR_DISPENSERS);
	const [erroredItems, setErroredItems] = useState({});

	function removeErroredItem(key) {
		setErroredItems(({ [key]: _, ...rest }) => rest);
	}

	function prepareDataTablePayload(data) {
		let tableData = {},
			conveyorDispensers = [],
			pumpDispensers = [],
			eprs = [],
			eprGroups = [];
		const inventoryData = data?.plan?.modules;
		for (const moduleId in inventoryData) {
			const moduleData = inventoryData[moduleId];
			for (const systemId in moduleData.systems) {
				let recordData = {
					moduleSymbol: moduleData.moduleSymbol,
					moduleId: moduleId,
					systemId: systemId,
					moduleType: moduleData.moduleType,
					layer: moduleData.layer,
					...moduleData.systems[systemId],
				};

				if (moduleData.systems[systemId]?.action === "undock") continue;

				switch (moduleData.moduleType) {
					case MODULE_TYPES.DISPENSER:
						const dispenserType = moduleData.systems[systemId]?.systemType;
						if (dispenserType === DISPENSER_TYPES.CONVEYOR) {
							conveyorDispensers.push(recordData);
						} else if (dispenserType === DISPENSER_TYPES.PUMP) {
							pumpDispensers.push(recordData);
						}
						break;

					case MODULE_TYPES.PLATE_RACKS:
						const eprGroupIndex = eprGroups.findIndex((item) => item.moduleId === moduleId);
						eprs.push(recordData);
						if (eprGroups[eprGroupIndex]) {
							eprGroups[eprGroupIndex].availableAmount += moduleData.systems[systemId]?.amountToBeFilled;
						} else {
							eprGroups.push({
								moduleSymbol: moduleData.moduleSymbol,
								moduleId: moduleId,
								availableAmount: moduleData.systems[systemId]?.amountToBeFilled || 0,
								fillAll: recordData?.filledAmount ? true : false, //TODO: Fix this, ideally this should be only true when there the whole epr is selected.
							});
						}
						break;

					default:
						break;
				}
			}
		}

		tableData = {
			[TABS.CONVEYOR_DISPENSERS]: { records: conveyorDispensers },
			[TABS.PUMP_DISPENSERS]: { records: pumpDispensers },
			[TABS.EPRS]: { records: eprs, groups: eprGroups },
		};

		return tableData;
	}

	function renderActiveTab(activeTab, helperFunctions, tableData) {
		const columns = generateTableColumns(activeTab, helperFunctions);
		const tabData = tableData?.[activeTab]?.groups || tableData?.[activeTab]?.records;
		let renderData = [];

		renderData.push(
			<DataTable
				customStyles={constrainedCustomDataTableStyles}
				key={activeTab}
				responsive={true}
				striped
				fixedHeader
				columns={columns}
				data={tabData}
			/>
		);

		return renderData;
	}

	function updateTableData(tab, moduleId, systemId, key, value) {
		if (!tableData?.[tab]) return;

		let newTableData = deepCopy(tableData[tab]);

		switch (tab) {
			case TABS.CONVEYOR_DISPENSERS:
			case TABS.PUMP_DISPENSERS:
				var tableRecordIndex = newTableData.records.findIndex(
					(record) => record.moduleId === moduleId && record.systemId === systemId
				);
				newTableData.records[tableRecordIndex][key] = value;

				break;

			case TABS.EPRS:
				let { groups, records } = newTableData;
				const eprGroupIndex = groups.findIndex((item) => item.moduleId === moduleId);
				groups[eprGroupIndex][key] = value;

				if (key === "fillAll") {
					for (const recordId in records) {
						if (records[recordId].moduleId === moduleId) {
							records[recordId]["filledAmount"] = value ? 1 : 0;
						}
					}
				}
				break;

			default:
				break;
		}

		setTableData((prev) => ({ ...prev, [tab]: newTableData }));
	}

	function generateCreatePlanPayload(tableData) {
		let modules = {},
			payload = {};

		function extractDataFromTableRecords(recordData) {
			let modules = {};
			for (const recordIndex in recordData) {
				let { moduleId, systemId, filledAmount } = recordData[recordIndex];

				const moduleKey = String(moduleId);
				const systemKey = String(systemId);

				if (isNaN(filledAmount)) continue;

				const systemDetails = {
					filledAmount: filledAmount ?? 0,
				};

				// Update or create the module and system entries
				if (modules[moduleKey]) {
					modules[moduleKey]["systems"][systemKey] = systemDetails;
				} else {
					modules[moduleKey] = {
						systems: {
							[systemKey]: systemDetails,
						},
					};
				}
			}

			return modules;
		}

		for (const tab in tableData) {
			let modulesData = {};
			modulesData = { ...modulesData, ...extractDataFromTableRecords(tableData[tab].records) };
			modules = { ...modules, ...modulesData };
		}

		if (isEmpty(modules)) {
			return;
		}

		payload = {
			wfId: workflowId,
			fillingDetails: { modules },
		};

		return payload;
	}

	async function saveInventoryPlan(tableData) {
		const payload = generateCreatePlanPayload(tableData);
		if (!payload) return;
		const { data, error, metadata } = await RepenishmentPlanService.updateFillingDetails(payload);
		if (data) {
			// successNotification("Success");
			setNeedsUpdate(true);
		}
		if (error) {
			if (metadata) {
				let erroredItems = parseMetaData(metadata);
				setErroredItems((prev) => ({ ...prev, ...erroredItems }));
			} else {
				errorNotification("Something went wrong!", { autoClose: 1000, toastId: "auto-save-error" });
			}
		}
	}

	return (
		<Page className="no-scrollbar">
			<Block>
				<Button active={activeTab === TABS.CONVEYOR_DISPENSERS} onClick={() => setActiveTab(TABS.CONVEYOR_DISPENSERS)}>
					Solid Ingredients
				</Button>
				<Button active={activeTab === TABS.PUMP_DISPENSERS} onClick={() => setActiveTab(TABS.PUMP_DISPENSERS)}>
					Liquid Ingredients
				</Button>
				<Button active={activeTab === TABS.EPRS} onClick={() => setActiveTab(TABS.EPRS)}>
					Bowl Racks
				</Button>
			</Block>
			<Scrollable>{renderActiveTab(activeTab, { updateTableData, erroredItems, removeErroredItem }, tableData)}</Scrollable>
			<Autosave data={tableData} onSave={saveInventoryPlan} interval={500} saveOnUnmount={false} />
		</Page>
	);
};

export default UpdateInventoryPlan;
